import React from "react";
import {
Drawer,
List,
ListItemButton,
ListItemIcon,
ListItemText,
Divider,
Tooltip,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
Button,
Typography,
Box
} from "@mui/material";

import LogoutIcon from "@mui/icons-material/Logout";

import { useLocation, useNavigate } from "react-router-dom";
import { Colors } from "../utils/colors";
import { useDispatch, useSelector } from "react-redux";
import { logout as logoutAction } from "../features/auth/authSlice";
import { logoutAdminSession } from "../services/adminSession";
import { navigationItems } from "./adminNavigation";
import { broadcastAdminLogout } from "../utils/adminSessionTimeout";

function SidebarContent({ logoUrl, logoDimensions, getItemStyle, adminRole, isSuperAdmin, navigate, setLogoutOpen }) {
  return (
    <List sx={{ px: 1, py: 1 }}>
      <ListItemButton
        onClick={() => navigate("/admin/users")}
        sx={{ justifyContent: "center", py: 2.5, mb: 1 }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt="FlipLottery logo"
            sx={{ width: logoDimensions.width, height: logoDimensions.height, objectFit: "contain", maxWidth: "100%" }}
          />
        ) : (
          <Typography variant="h6" fontWeight={700} sx={{ color: Colors.accent }}>
            FlipLottery
          </Typography>
        )}
      </ListItemButton>

      {navigationItems.map((item) => {
        if (item.allowedRoles && !item.allowedRoles.includes(adminRole)) return null;
        const Icon = item.icon;
        const isDisabled = item.superAdminOnly && !isSuperAdmin;

        return (
          <Tooltip key={item.path} title={isDisabled ? "Only super admin allowed" : ""}>
            <span>
              <ListItemButton
                onClick={() => !isDisabled && navigate(item.path)}
                sx={{
                  ...getItemStyle(item.path, isDisabled),
                  borderRadius: 2,
                  py: 1.25,
                  px: 1.5,
                  mb: 0.5
                }}
              >
                <ListItemIcon sx={{ color: "whitesmoke", minWidth: 42 }}>
                  <Icon />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: { xs: 15, lg: 16 }
                  }}
                />
              </ListItemButton>
            </span>
          </Tooltip>
        );
      })}

      <Divider sx={{ background: "rgba(255,255,255,0.2)", my: 1.5 }} />

      <ListItemButton
        onClick={() => setLogoutOpen(true)}
        sx={{
          borderRadius: 2,
          py: 1.25,
          px: 1.5,
          "&:hover": { backgroundColor: "rgba(255,0,0,0.2)" }
        }}
      >
        <ListItemIcon sx={{ color: "whitesmoke", minWidth: 42 }}>
          <LogoutIcon />
        </ListItemIcon>
        <ListItemText primary="Logout" />
      </ListItemButton>
    </List>
  );
}

export default function Sidebar({ mobileOpen = false, onClose = () => {} }){

const navigate = useNavigate()
const location = useLocation()
const dispatch = useDispatch()


const admin = useSelector((state)=>state.auth.admin);
const branding = useSelector((state)=>state.branding);
const isSuperAdmin = admin === "super_admin" ? true :false;
const [logoutOpen,setLogoutOpen] = React.useState(false);
const logoUrl = branding?.logoUrl || "";
const width = Number(branding?.logoWidth) || 96;
const height = Number(branding?.logoHeight) || 96;
const logoDimensions = {
  width: Math.max(120, Math.round(width * 1.25)),
  height: Math.max(60, Math.round(height * 1.25))
};

const logout = async () => {
try{
await logoutAdminSession()
}catch(error){
console.log(error)
}

dispatch(logoutAction())
broadcastAdminLogout({ reason: "manual" });
setLogoutOpen(false)
navigate("/")
}


const disabledStyle = {
opacity:0.4,
pointerEvents:"none"
}

const activeItemStyle = {
backgroundColor:"rgba(77, 163, 255, 0.22)",
borderLeft:"4px solid #4da3ff",
"& .MuiListItemIcon-root":{
color:"#7fc4ff"
},
"& .MuiListItemText-primary":{
color:"#7fc4ff",
fontWeight:700
}
}

const getItemStyle = (path, isDisabled = false) => ({
borderLeft:"4px solid transparent",
"&:hover":{backgroundColor:"rgba(255,255,255,0.08)"},
...(location.pathname === path ? activeItemStyle : {}),
...(isDisabled ? disabledStyle : {})
})

return(
<>
<Drawer
variant="temporary"
open={mobileOpen}
onClose={onClose}
ModalProps={{ keepMounted: true }}
sx={{
display:{ xs:"block", lg:"none" },
"& .MuiDrawer-paper": {
width:"var(--admin-sidebar-width)",
backgroundColor: Colors.secondary,
color:"whitesmoke",
borderRight:"none"
}
}}
>
<SidebarContent
logoUrl={logoUrl}
logoDimensions={logoDimensions}
getItemStyle={getItemStyle}
isSuperAdmin={isSuperAdmin}
adminRole={admin}
navigate={(path) => {
  navigate(path);
  onClose();
}}
setLogoutOpen={setLogoutOpen}
/>
</Drawer>

<Drawer
variant="permanent"
sx={{
display:{ xs:"none", lg:"block" },
width:{ lg:"var(--admin-sidebar-width)" },
flexShrink:{ lg:0 },
"& .MuiDrawer-paper": {
width:"var(--admin-sidebar-width)",
backgroundColor: Colors.secondary,
color:"whitesmoke",
borderRight:"none",
boxSizing:"border-box"
}
}}
open
>
<SidebarContent
logoUrl={logoUrl}
logoDimensions={logoDimensions}
getItemStyle={getItemStyle}
isSuperAdmin={isSuperAdmin}
adminRole={admin}
navigate={navigate}
setLogoutOpen={setLogoutOpen}
/>
</Drawer>

<Dialog open={logoutOpen} onClose={()=>setLogoutOpen(false)} fullWidth maxWidth="xs">
<DialogTitle>Logout</DialogTitle>
<DialogContent>
<Typography>
Do you want to log out from the admin dashboard?
</Typography>
</DialogContent>
<DialogActions>
<Button onClick={()=>setLogoutOpen(false)}>Cancel</Button>
<Button variant="contained" color="error" onClick={logout}>
Logout
</Button>
</DialogActions>
</Dialog>

</>

)

}
